import type { SimCareerView } from '@cruz/shared';
import type { ReactNode } from 'react';
import { HeaderShell, headerButton } from '../components/Header.tsx';
import { AutoIcon, MoonIcon, SunIcon } from '../components/icons.tsx';
import type { ThemeMode } from '../hooks/useTheme.ts';
import { brotherhoodName, num, plural, upperFirst } from './format.ts';
import { navigate } from '../challenge/router.ts';

export { inputClass, Notice, primaryButton, secondaryButton } from '../challenge/ui.tsx';

/** Pieces shared by the simulator screens. */

export const card =
  'rounded-2xl border border-purple/15 bg-paper shadow-[0_1px_0_rgba(72,32,82,0.04)] ' +
  'dark:border-cream/15 dark:bg-night-800';

export const linkClass =
  'font-medium text-purple underline decoration-gold underline-offset-4 hover:decoration-2 ' +
  'dark:text-gold-300';

export const SettingsIcon = () => (
  <svg
    width={22}
    height={22}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
  </svg>
);

export function SimLayout({
  theme,
  onTheme,
  account,
  children,
  wide = false,
}: {
  theme: ThemeMode;
  onTheme: () => void;
  /** Show the account button (signed in) */
  account?: boolean;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="sim-figures flex min-h-dvh flex-col">
      <HeaderShell
        title="De cirio a hermano mayor"
        subtitle="Un juego de Cruz de IA"
        titleAs="p"
        titleClass="text-[1.22rem] max-[399px]:tracking-normal min-[400px]:text-[1.4rem] sm:text-[1.65rem]"
      >
        {/* On a small phone the title needs the room: signed in, the theme is in Tu cuenta */}
        <button
          type="button"
          className={`${headerButton} ${account ? 'max-[399px]:hidden' : ''}`}
          onClick={onTheme}
          aria-label="Cambiar tema"
          title="Cambiar tema"
        >
          {theme === 'auto' ? <AutoIcon /> : theme === 'light' ? <SunIcon /> : <MoonIcon />}
        </button>
        {account && (
          <a
            href="/simulador/ajustes"
            className={headerButton}
            aria-label="Tu cuenta"
            title="Tu cuenta"
            onClick={(e) => {
              e.preventDefault();
              navigate('/simulador/ajustes');
            }}
          >
            <SettingsIcon />
          </a>
        )}
      </HeaderShell>
      <main
        className={`animate-rise mx-auto w-full ${wide ? 'max-w-2xl' : 'max-w-xl'} flex-1 px-4 py-5 pb-[calc(6rem+env(safe-area-inset-bottom))]`}
      >
        {children}
      </main>
    </div>
  );
}

/** A link inside the app, without reloading the page. */
export function Link({
  to,
  className = linkClass,
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
        scrollTo({ top: 0 });
      }}
    >
      {children}
    </a>
  );
}

export function PageTitle({ children, kicker }: { children: ReactNode; kicker?: string }) {
  return (
    <div className="mb-5">
      {kicker && (
        <p className="mb-1 text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
          {kicker}
        </p>
      )}
      <h1 className="font-serif text-[2rem] leading-tight font-semibold text-purple dark:text-cream">
        {children}
      </h1>
    </div>
  );
}

/** Three dots while the server works (an event written live takes a few seconds). */
export function Working({ label }: { label: string }) {
  return (
    <div role="status" className="flex items-center gap-3 py-6 text-ink-soft dark:text-cream/75">
      <span className="flex gap-1" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="animate-dot h-2 w-2 rounded-full bg-gold"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </span>
      <span>{label}</span>
    </div>
  );
}

/** A 0–100 bar. */
export function Meter({ value, label }: { value: number; label: string }) {
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      className="h-1.5 overflow-hidden rounded-full bg-purple/10 dark:bg-cream/10"
    >
      <div
        className="h-full rounded-full bg-gold transition-[width] duration-700 ease-out"
        style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
      />
    </div>
  );
}

const SHORT: Record<string, string> = {
  devotion: 'Devoción',
  prestige: 'Prestigio',
  board: 'Junta',
  stamina: 'Aguante',
  wallet: 'Bolsillo',
};

/** Always on top of the game: who you are in your brotherhood and the five stats. */
export function StatusBar({ career }: { career: SimCareerView }) {
  const p = career.player;
  const b = career.brotherhood;
  return (
    <section aria-label="Tu situación" className={`${card} mb-4 px-4 py-3`}>
      <p className="truncate font-serif text-xl leading-tight font-semibold text-purple dark:text-cream">
        {upperFirst(brotherhoodName(b))}
      </p>
      <p className="mt-0.5 text-sm text-ink-soft dark:text-cream/75">
        <span className="font-semibold text-gold-700 dark:text-gold-300">
          {p.rank_label}
          {p.post ? ` · ${p.post}` : ''}
        </span>
        {/* Each piece whole on its line: never "tramo 1 / de 5" */}
        {[
          `nº ${num(p.member_number)}`,
          plural(p.seniority_years, 'año', 'años'),
          p.tramo !== null ? `tramo ${p.tramo} de ${p.tramos}` : null,
        ]
          .filter(Boolean)
          .map((t) => (
            <span key={t}>
              {' · '}
              <span className="whitespace-nowrap">{t}</span>
            </span>
          ))}
      </p>
      <dl className="mt-3 grid grid-cols-5 gap-1.5 min-[400px]:gap-2">
        {p.stats.map((s) => (
          <div key={s.key} className="min-w-0">
            <dt className="truncate text-[0.72rem] font-medium text-ink-soft dark:text-cream/70">
              {SHORT[s.key] ?? s.label}
            </dt>
            <dd className="tabular text-base font-semibold">
              <span className="sr-only">{s.label}: </span>
              {s.value}
              <Meter value={s.value} label={s.label} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const TABS = [
  { tab: 'hoy', label: 'Hoy', path: '/simulador' },
  { tab: 'hermandad', label: 'Hermandad', path: '/simulador/hermandad' },
  { tab: 'vida', label: 'Mi vida', path: '/simulador/vida' },
  { tab: 'personajes', label: 'Personajes', path: '/simulador/personajes' },
  { tab: 'logros', label: 'Logros', path: '/simulador/logros' },
] as const;

/** Bottom navigation, for one thumb. */
export function TabBar({ current, badge }: { current: string; badge?: boolean }) {
  return (
    <nav
      aria-label="Secciones"
      className="fixed inset-x-0 bottom-0 z-10 border-t border-gold/60 bg-paper/95 pb-[env(safe-area-inset-bottom)] backdrop-blur dark:border-gold/30 dark:bg-night-800/95"
    >
      <ul className="mx-auto grid max-w-xl grid-cols-5">
        {TABS.map((t) => {
          const active = current === t.tab;
          return (
            <li key={t.tab}>
              <a
                href={t.path}
                aria-current={active ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(t.path);
                  scrollTo({ top: 0 });
                }}
                className={`relative flex min-h-14 flex-col items-center justify-center px-1 text-[0.8rem] font-medium transition-colors ${
                  active
                    ? 'text-purple dark:text-gold-300'
                    : 'text-ink-soft hover:text-purple dark:text-cream/70 dark:hover:text-cream'
                }`}
              >
                <span
                  className={`mb-1 h-0.5 w-6 rounded-full ${active ? 'bg-gold' : 'bg-transparent'}`}
                  aria-hidden
                />
                {t.label}
                {t.tab === 'hoy' && badge && (
                  <span className="absolute top-2 right-[30%] h-2 w-2 rounded-full bg-burgundy dark:bg-burgundy-300">
                    <span className="sr-only"> (te esperan eventos)</span>
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
